<script setup>
/* Coluna esquerda da tela "Simulação e Digitação": todos os clientes já
   consultados, PERSISTIDOS no simulador-api (GET /simulacoes) — fechar e
   abrir o painel não perde nada. Busca é server-side (com debounce). */
import { computed, ref } from 'vue';
import { brl, cpfFmt } from '../bancos.js';

const props = defineProps({
  simulacoes: { type: Array, default: () => [] },
  ativaId: { type: [String, Number], default: null },
  carregando: { type: Boolean, default: false },
});
const emit = defineEmits(['nova', 'selecionar', 'limpar-todas', 'busca']);

const busca = ref('');
const filtro = ref('todas'); // 'todas' | 'com-oferta'
let buscaTimer = null;

const onBusca = () => {
  clearTimeout(buscaTimer);
  buscaTimer = setTimeout(() => emit('busca', busca.value.trim()), 350);
};

/* Paleta determinística do avatar: mesma cor para o mesmo nome, sempre. */
const AVATAR_CORES = [
  '#F26E21', '#7C3AED', '#0576C7', '#16A34A', '#E92063', '#0DB9F2', '#D97706',
];
const corDoNome = nome => {
  let h = 0;
  for (const c of String(nome || '')) h = (h * 31 + c.charCodeAt(0)) % 9973;
  return AVATAR_CORES[h % AVATAR_CORES.length];
};
const iniciais = nome =>
  String(nome || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();

const hora = ts => {
  const d = new Date(ts || Date.now());
  const hoje = new Date();
  const mesmoDia =
    d.getDate() === hoje.getDate() &&
    d.getMonth() === hoje.getMonth() &&
    d.getFullYear() === hoje.getFullYear();
  if (mesmoDia)
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const visiveis = computed(() =>
  props.simulacoes.filter(s =>
    filtro.value === 'com-oferta' ? (s.qtdOfertas || 0) > 0 : true
  )
);

/* Linha de status do item: espelha o que os cards estão mostrando. */
const statusDe = s => {
  if (s.consultando) return { texto: 'Consultando…', classe: 'text-n-amber-11', girando: true };
  if (s.melhorOferta)
    return { texto: `Melhor oferta ${brl(s.melhorOferta)}`, classe: 'text-n-teal-11', girando: false };
  return { texto: 'Sem oferta', classe: 'text-n-slate-11', girando: false };
};
</script>

<template>
  <div class="flex flex-col w-full gap-3">
    <button type="button" class="sd-btn-primary w-full" @click="emit('nova')">
      <span class="i-lucide-plus size-4" />
      Nova simulação
    </button>

    <div class="relative">
      <span
        class="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-n-slate-11 i-lucide-search"
      />
      <input
        v-model="busca"
        type="text"
        placeholder="Buscar por CPF, telefone ou nome"
        class="sd-input pl-9"
        @input="onBusca"
      />
    </div>

    <div class="flex items-center justify-between">
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          class="sd-chip"
          :class="filtro === 'todas' ? 'is-on' : ''"
          @click="filtro = 'todas'"
        >
          Todas
        </button>
        <button
          type="button"
          class="sd-chip"
          :class="filtro === 'com-oferta' ? 'is-on' : ''"
          @click="filtro = 'com-oferta'"
        >
          Com oferta
        </button>
      </div>
      <button
        type="button"
        class="text-xs transition-colors text-n-slate-11 hover:text-n-ruby-11 disabled:opacity-50"
        :disabled="!simulacoes.length"
        @click="emit('limpar-todas')"
      >
        Limpar todas
      </button>
    </div>

    <!-- Estado vazio: borda tracejada, igual ao original. -->
    <div v-if="!visiveis.length" class="sd-vazio">
      <div
        class="flex items-center justify-center rounded-full size-12 bg-n-alpha-2 text-n-slate-11"
      >
        <span
          :class="carregando ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-search'"
          class="size-6"
        />
      </div>
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ carregando ? 'Carregando…' : 'Sem simulações' }}
        </h3>
        <p v-if="!carregando" class="text-sm text-n-slate-11">
          Preencha o formulário e clique em Simular — os clientes consultados
          ficam salvos aqui.
        </p>
      </div>
    </div>

    <ul
      v-else
      class="flex flex-col overflow-hidden overflow-y-auto border rounded-lg border-n-weak bg-n-solid-2 max-h-[540px]"
    >
      <li v-for="(s, i) in visiveis" :key="s.id">
        <button
          type="button"
          class="flex items-start w-full gap-3 px-3 py-3 text-left transition-colors hover:bg-n-alpha-2"
          :class="[
            i > 0 ? 'border-t border-n-weak' : '',
            ativaId === s.id ? 'bg-n-alpha-2' : '',
          ]"
          @click="emit('selecionar', s.id)"
        >
          <span
            class="flex items-center justify-center text-xs font-semibold text-white rounded-full size-9 shrink-0"
            :style="{ backgroundColor: corDoNome(s.nome || s.cpf) }"
          >
            {{ iniciais(s.nome || '?') }}
          </span>
          <span class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span class="flex items-center justify-between gap-2">
              <span class="text-sm font-semibold truncate text-n-slate-12">
                {{ s.nome || 'Sem nome' }}
              </span>
              <span class="text-xs shrink-0 text-n-slate-11">{{ hora(s.atualizadaEm || s.criadaEm) }}</span>
            </span>
            <span
              class="flex items-center gap-1 text-xs font-medium truncate"
              :class="statusDe(s).classe"
            >
              <span
                v-if="statusDe(s).girando"
                class="i-lucide-loader-2 size-3 animate-spin"
              />
              {{ statusDe(s).texto }}
            </span>
            <span class="text-xs truncate text-n-slate-11 tabular-nums">
              {{ cpfFmt(s.cpf) }}<template v-if="s.celular"> · {{ s.celular }}</template>
            </span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>
